import type {
  DisclaimerInterpolationParam,
  PaymentModel,
  PlanDisclaimer,
  SubscriptionModel,
} from '../types/SubscriptionModel';

type Translate = (key: string, params?: any[] | Record<string, any>) => string;

const REFUND_URL = '/refund.html';
const WEEK_PERIOD_KEY = 'subscription_period_1week';
const DEFAULT_WEEKLY_RENEWAL_PERIOD_KEY = 'subscription_period_1month';

type DiscountOptions = {
  useRenewalPrice?: boolean;
  percentRounding?: DiscountPercentRounding;
};

type DiscountPercentRounding = 'marketing' | 'floor';

function getDiscountBasePrice(
  plan: SubscriptionModel,
  payment: PaymentModel | undefined,
  options: DiscountOptions = {},
): number {
  const fullPrice = plan.fullPrice ?? 0;

  return options.useRenewalPrice ? (payment?.renewalPrice ?? plan.renewalPrice ?? fullPrice) : fullPrice;
}

export function calculateDiscountAmount(
  plan: SubscriptionModel,
  payment: PaymentModel | undefined,
  options: DiscountOptions = {},
): number {
  const basePrice = getDiscountBasePrice(plan, payment, options);
  const discountPrice = payment?.discPrice ?? 0;

  return roundTo(basePrice - discountPrice, 2);
}

export function calculateDiscountPercent(
  plan: SubscriptionModel,
  payment: PaymentModel | undefined,
  options: DiscountOptions = {},
): number {
  return calculateDiscountPercentFromPrices(
    getDiscountBasePrice(plan, payment, options),
    payment?.discPrice ?? 0,
    options.percentRounding,
  );
}

export function calculateDiscountPercentFromPrices(
  fullPrice: number,
  discPrice: number,
  rounding: DiscountPercentRounding = 'marketing',
): number {
  if (!Number.isFinite(fullPrice) || !Number.isFinite(discPrice) || !fullPrice) {
    return 0;
  }

  const rawPercent = ((fullPrice - discPrice) / fullPrice) * 100;
  if (rounding === 'floor') {
    return Math.floor(rawPercent);
  }

  const oneDecimal = roundTo(rawPercent, 1);

  if (oneDecimal % 1 === 0.5) {
    return rawPercent < 40 ? oneDecimal : Math.ceil(oneDecimal);
  }

  // Existing UI mostly shows whole-number discounts, except lower-discount half-step values such as 35.5.
  return Math.round(rawPercent);
}

export function resolveLandingSubscriptionDisclaimer(
  selectedPlan: SubscriptionModel,
  t: Translate,
  isSuperDiscount = false,
  isExpired = false,
): string {
  const disclaimer = resolveLandingDisclaimerConfig(selectedPlan, isSuperDiscount, isExpired);

  return translateDisclaimer(disclaimer, t);
}

export function resolveUpsaleDisclaimer(
  currentPage: string,
  plan: SubscriptionModel | null,
  t: Translate,
): string {
  if (isCourseUpsalePage(currentPage)) {
    return translateDisclaimer(
      resolveCourseDisclaimerConfig(plan, currentPage === 'super-price'),
      t,
    );
  }

  return translateDisclaimer(
    resolveFitnessDisclaimerConfig(plan, currentPage === 'super-discount'),
    t,
  );
}

function resolveLandingDisclaimerConfig(
  plan: SubscriptionModel,
  isSuperDiscount: boolean,
  isExpired: boolean,
): PlanDisclaimer {
  const payment = resolveLandingPayment(plan, isSuperDiscount, isExpired);

  if (!payment) {
    throw new Error(`Landing payment metadata is missing for plan ${plan.id}`);
  }

  const defaultDisclaimer: PlanDisclaimer = {
    translationKey: plan.periodKey === WEEK_PERIOD_KEY
      ? 'lp_v7_subscription_terms_1week'
      : 'lp_v7_subscription_terms_1and3_months',
    interpolationParams: [
      { i18nKey: plan.periodKey },
      formatMoney(payment.renewalPrice ?? plan.renewalPrice ?? plan.weeklyRenewalPrice ?? plan.fullPrice, plan.currencyCode),
      REFUND_URL,
      formatMoney(payment.discPrice, plan.currencyCode),
      { i18nKey: plan.renewalPeriodKey ?? (plan.periodKey === WEEK_PERIOD_KEY ? DEFAULT_WEEKLY_RENEWAL_PERIOD_KEY : plan.periodKey) },
    ],
  };

  return payment.disclaimer ?? plan.landingDisclaimer ?? defaultDisclaimer;
}

function resolveLandingPayment(
  plan: SubscriptionModel,
  isSuperDiscount: boolean,
  isExpired: boolean,
): PaymentModel | undefined {
  if (isExpired) {
    return plan.expired as PaymentModel | undefined;
  }

  if (isSuperDiscount) {
    return plan.super;
  }

  return plan.standard;
}

function resolveFitnessDisclaimerConfig(
  plan: SubscriptionModel | null,
  isFitnessDiscount: boolean,
): PlanDisclaimer {
  if (!plan?.fitness) {
    throw new Error(`Fitness disclaimer metadata is missing for plan ${plan?.id ?? 'unknown'}`);
  }

  const { fitness } = plan;
  const payment = isFitnessDiscount ? fitness.super : fitness.standard;

  if (payment.disclaimer) {
    return payment.disclaimer;
  }

  return {
    translationKey: plan.periodKey === WEEK_PERIOD_KEY
      ? 'fitness_subscription_terms_1week'
      : 'fitness_subscription_terms_1and3_months',
    interpolationParams: [
      REFUND_URL,
      { i18nKey: plan.periodKey },
      formatMoney(payment.discPrice, fitness.currencyCode),
      { i18nKey: fitness.periodKey },
    ],
  };
}

function resolveCourseDisclaimerConfig(
  plan: SubscriptionModel | null,
  isDiscount: boolean,
): PlanDisclaimer {
  const disclaimer = isDiscount
    ? plan?.super?.disclaimer
    : plan?.standard.disclaimer;

  if (disclaimer) {
    return disclaimer;
  }

  return {
    translationKey: 'oldTranslations.upsale.courses.disclaimer',
    interpolationParams: ['', ''],
  };
}

function isCourseUpsalePage(currentPage: string): boolean {
  return currentPage === 'sexology'
    || currentPage === 'mental-side'
    || currentPage === 'super-price';
}

function translateDisclaimer(disclaimer: PlanDisclaimer, t: Translate): string {
  return t(disclaimer.translationKey, resolveDisclaimerInterpolationParams(disclaimer.interpolationParams, t));
}

function resolveDisclaimerInterpolationParams(params: DisclaimerInterpolationParam[], t: Translate): string[] {
  return params.map((param) => {
    if (typeof param === 'object' && 'i18nKey' in param) {
      return t(param.i18nKey);
    }

    return `${param}`;
  });
}

function formatMoney(price: number, currencyCode: 'USD' | 'EUR'): string {
  const amount = Number.isInteger(price) ? `${price}` : price.toFixed(2);

  return `${amount} ${currencyCode}`;
}

function roundTo(value: number, precision: number): number {
  const factor = 10 ** precision;

  return Math.round((value + Number.EPSILON) * factor) / factor;
}
